// src/core/randomizer.ts
import { type DitherParams } from './types';
import { ALL_ALGORITHMS } from '../algorithms';
import { BUILTIN_PALETTES } from '../palettes/builtin';

const pick = <T>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)]!;
const range = (min: number, max: number, step = 1): number => {
  const steps = Math.floor((max - min) / step) + 1;
  return min + Math.floor(Math.random() * steps) * step;
};

/**
 * Produce a fresh set of "visually interesting" params, preserving the user's
 * output/downscale preference. Excludes the `none` style so the result is
 * always actually dithered.
 */
export function randomizeParams(current: DitherParams): DitherParams {
  const styles = ALL_ALGORITHMS.filter(a => a.id !== 'none');
  const style = pick(styles);
  const tileMin = style.tileRange?.min ?? 1;
  const tileMax = style.tileRange?.max ?? 1;
  return {
    ...current,                              // keep output/downscale settings
    styleId: style.id,
    scale: range(1, 12),                     // bias to lower scales (chunkier but visible)
    tile: range(tileMin, tileMax),
    paletteId: pick(BUILTIN_PALETTES).id,
    invert: Math.random() < 0.2,
    contrast: range(-30, 50),
    midtones: 0.5 + Math.random() * 1.5,     // 0.5..2.0
    highlights: range(-20, 40),
    blur: Math.random() < 0.3 ? Math.random() * 2 : 0,  // mostly no blur
    threshold: range(50, 200),
    thresholdEnabled: Math.random() < 0.1,   // rarely on
  };
}
